import clsx from "clsx";
import { FunctionComponent } from "react";

import type {
  ContentCardBaseConfig,
  ContentCardArticle,
} from "@ui/components/content-cards/content-cards.types";
import ContentBlock, {
  UpperMeta,
} from "@ui/components/content-cards/ContentBlock/ContentBlock";
import ContentCardScore from "@ui/components/content-cards/ContentCardScore/ContentCardScore";
import Image from "@ui/components/media/Image";
import MediaIcon from "@ui/components/media/MediaIcon";
import { thumbnailContainerContext } from "@ui/components/media/MediaIcon/MediaIcon.css";
import CommentCallout from "@ui/components/meta/CommentCallout";
import DuetLink from "@ui/components/navigation/DuetLink";
import SELECTORS from "@ui/config/class-selectors.config";
import { useAnalyticsEventMetadataContext } from "@ui/context/analytics-event-metadata-context";
import {
  combineComponentContexts,
  useComponentConfig,
} from "@ui/context/component-config-context";
import { useContentCardAttributeContext } from "@ui/context/content-card-attribute-context";
import { useDataAttributes } from "@ui/hooks/use-data-attributes";
import useDefaults from "@ui/hooks/use-defaults";
import { sharedStyles } from "@ui/shared-styles";
import { getContentCardPosition } from "@ui/utils/get-content-card-position";
import { useAnalytics } from "@voxmedia/duet-context/context/analytics-context";
import { useGlobalDataContext } from "@voxmedia/duet-context/context/global-data-context";
import { getPageType } from "@voxmedia/duet-utils/analytics";

import * as styles from "./CompactContentCard.css";

export interface CompactContentCardBaseConfig extends ContentCardBaseConfig {
  imagePosition: "left" | "right" | "floatRight" | "hideImage";
  imageAspectRatio: "square" | "squareMobile" | "wide" | "semiWide";
  alignTop: boolean;
  extraImageStyles?: string;
}

interface Props
  extends ContentCardArticle, Partial<CompactContentCardBaseConfig> {
  componentContext?: string;
  gaComponentName: string;
  firstChild?: boolean;
  lastChild?: boolean;
  showBorderOnLastContentCard?: boolean;
  className?: string;
  priorityImage?: boolean;
  contentBlockContainerClassName?: string;
  contentBlockClassName?: string;
  contentBlockInnerClassName?: string;
  contentBlockHeadlineContainerClassName?: string;
  contentBlockHeadlineClassName?: string;
  contentBlockDekClassName?: string;
  contentBlockUpperMetaCategoryClassName?: string;
  contentBlockLowerMetaClassName?: string;
}

const CompactContentCard: FunctionComponent<Props> = (props) => {
  const { componentContext = "compactContentCard" } = props;

  const childComponentContext = combineComponentContexts([
    "compactContentCard",
    componentContext,
  ]);
  const contentCardConfig = useComponentConfig(
    "compactContentCard",
    componentContext,
  );

  const analytics = useAnalytics();
  const { metadata: analyticsMetadata } = useAnalyticsEventMetadataContext();

  const { placeholderImageUrl } = useGlobalDataContext().siteSettings;

  const defaults = useDefaults(props, contentCardConfig);
  const {
    adFlag,
    advertiserName,
    alignTop,
    authors,
    authorByLine,
    category,
    chorusId,
    chorusUuid,
    commentCount,
    commentCountId,
    commentUrl,
    comments,
    contentBlockClassName,
    contentBlockContainerClassName,
    contentBlockHeadlineContainerClassName,
    contentBlockHeadlineClassName,
    contentBlockDekClassName,
    contentBlockLowerMetaClassName,
    contentBlockUpperMetaCategoryClassName,
    contentBlockInnerClassName,
    contentBlockSize,
    contentBlockVariation,
    contentType,
    coverLink,
    crossPostSource,
    crossPostUrl,
    dek,
    extraImageStyles,
    firstChild,
    gaComponentName,
    globalId,
    headline,
    id,
    imageAspectRatio,
    imagePosition,
    lastChild,
    latestRecircPadding,
    linkClassName,
    live,
    liveBadgeName,
    lowerMetaFirstComponent,
    lowerMetaSecondComponent,
    lowerMetaThirdComponent,
    mediaIconInset,
    mediaVariation,
    mediaVariationIconPlacement,
    metaVariation,
    packageName,
    permalink,
    postTemplate,
    priorityImage,
    score,
    showAuthorBy,
    showCommentCallout,
    showBorderOnLastContentCard,
    showDek,
    showScore,
    site,
    streamName,
    thumbnails,
    timestamp,
    upperMetaFirstComponent,
    upperMetaSecondComponent,
    upperMetaThirdComponent,
  } = defaults;

  let upperMetaFirstComponentResolved = upperMetaFirstComponent;

  // Automatically determine first upper meta component if not specified.
  if (upperMetaFirstComponentResolved === undefined) {
    if (adFlag) {
      upperMetaFirstComponentResolved = "ContentBlockAdvertiserFlag";
    } else if (contentType === "map") {
      upperMetaFirstComponentResolved = "ContentBlockMap";
    } else if (packageName) {
      upperMetaFirstComponentResolved = "ContentBlockPackage";
    } else if (crossPostSource && !site?.isCrossCommunity) {
      upperMetaFirstComponentResolved = "ContentBlockCrossPost";
    } else if (live) {
      upperMetaFirstComponentResolved = "LiveBadge";
    } else {
      upperMetaFirstComponentResolved = "Category";
    }
  }

  const contentBlockProps = {
    adFlag,
    advertiserName,
    authors,
    authorByLine,
    category,
    chorusId,
    chorusUuid,
    className: contentBlockClassName,
    commentCount,
    commentCountId,
    commentUrl,
    componentContext: childComponentContext,
    contentBlockSize,
    contentBlockVariation,
    contentType,
    coverLink,
    crossPostSource,
    crossPostUrl,
    dek,
    gaComponentName,
    globalId,
    headline,
    headlineContainerClassName: contentBlockHeadlineContainerClassName,
    headlineClassName: contentBlockHeadlineClassName,
    dekClassName: contentBlockDekClassName,
    id,
    innerClassName: contentBlockInnerClassName,
    linkClassName,
    live,
    liveBadgeName,
    lowerMetaFirstComponent,
    lowerMetaSecondComponent,
    lowerMetaThirdComponent,
    mediaVariation,
    mediaVariationIconPlacement,
    metaVariation,
    packageName,
    permalink,
    postTemplate,
    showAuthorBy,
    showDek,
    site,
    streamName,
    timestamp,
    upperMetaCategoryClassName: contentBlockUpperMetaCategoryClassName,
    upperMetaFirstComponent: upperMetaFirstComponentResolved,
    upperMetaSecondComponent,
    upperMetaThirdComponent,
  };

  const thumbnail = (
    <div
      className={clsx(
        styles.imageContainer[imagePosition],
        styles.imageAspectRatio[imageAspectRatio],
        styles.imageBorderRadius,
        alignTop && styles.alignTop,
        thumbnailContainerContext,
        extraImageStyles,
      )}
    >
      {imageAspectRatio === "wide" ? (
        <Image
          sizes="221px"
          src={thumbnails?.horizontal || placeholderImageUrl.horizontal}
          priority={!!priorityImage}
          alt={headline}
        />
      ) : imageAspectRatio === "square" ? (
        <div>
          <Image
            sizes="100px"
            src={thumbnails?.square || placeholderImageUrl.square}
            priority={!!priorityImage}
            alt={headline}
          />
        </div>
      ) : (
        <>
          <Image
            sizes="100px"
            src={thumbnails?.square || placeholderImageUrl.square}
            priority={!!priorityImage}
            alt={headline}
            className={styles.squareMobileSquareImage}
          />
          <Image
            sizes="195px"
            src={thumbnails?.horizontal || placeholderImageUrl.horizontal}
            priority={!!priorityImage}
            alt={headline}
            className={styles.squareMobileHorizontalImage}
          />
        </>
      )}
      {mediaVariation && mediaVariationIconPlacement === "thumbnail" && (
        <MediaIcon
          placement="thumbnail"
          variation={mediaVariation}
          inset={mediaIconInset}
        />
      )}
      {score && showScore && <ContentCardScore score={score} />}
      {comments?.enabled && showCommentCallout && commentCountId && (
        <CommentCallout countId={commentCountId} />
      )}
    </div>
  );

  const { generateContentCardAttributes } = useContentCardAttributeContext();

  return (
    <div
      {...useDataAttributes(props)}
      {...generateContentCardAttributes(props)}
      className={clsx(
        props.className,
        SELECTORS.ContentCard,
        styles.cardLayout[imagePosition],
        coverLink
          ? sharedStyles.contentCard.coverLinkContentCardContainer
          : sharedStyles.contentCard.contentCardContainer,
        firstChild && styles.firstChild,
        lastChild && styles.lastChild,
        lastChild && !showBorderOnLastContentCard && styles.hideBorder,
        latestRecircPadding && styles.cardLayout.latestRecirc,
      )}
    >
      {imagePosition === "floatRight" && (
        <div className={styles.floatRightMetaContainerUpper}>
          <UpperMeta {...contentBlockProps} />
        </div>
      )}

      {imagePosition !== "hideImage" &&
        (coverLink ? (
          thumbnail
        ) : (
          <DuetLink
            href={permalink}
            hrefSiteData={site}
            className={sharedStyles.contentCard.thumbnailLink}
            onClick={(e) => {
              analytics?.emit({
                event: "article_click",
                article_click: {
                  article_component_name: gaComponentName,
                  article_uri: permalink,
                  article_title: headline,
                  author: authors[0]?.name ?? authorByLine,
                  page_type: getPageType(contentType, !!postTemplate),
                  position: getContentCardPosition(e.currentTarget),
                  ...analyticsMetadata,
                },
              });
            }}
          >
            {thumbnail}
          </DuetLink>
        ))}
      <div
        className={clsx(
          styles.contentBlockContainer,
          contentBlockContainerClassName,
          alignTop && styles.alignTop,
        )}
      >
        <ContentBlock
          {...contentBlockProps}
          upperMetaClassName={clsx(
            imagePosition === "floatRight" &&
              styles.floatRightMetaContainerInner,
          )}
          lowerMetaClassName={contentBlockLowerMetaClassName}
        />
      </div>
    </div>
  );
};

export default CompactContentCard;
